import { useEffect, useMemo, useRef } from 'react';
import useAuth from 'src/hooks/useAuth';
import { GROUP_LEVEL_ACCESS } from 'src/components/access';
import { IStore } from 'src/interfaces/store';
import { IUser } from 'src/interfaces/user';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { TSelectedLead } from '../types';

type TAssistantStore = Partial<IStore> & { businessAssistantEnabled?: boolean };
type TAssistantUser = IUser & {
    businessAssistantEnabled?: boolean;
    stores?: TAssistantStore[];
    group?: {
        _id?: string;
        businessAssistantEnabled?: boolean;
        stores?: TAssistantStore[];
    };
};

type TAccessCache = ReadonlyMap<string, boolean>;

/**
 * Merges the stores that report `businessAssistantEnabled` into the known access,
 * keeping previous values for stores that come without the flag (e.g. a lead
 * update that returns the store unpopulated).
 */
export const mergeStoreAccess = (
    known: TAccessCache,
    stores: Array<string | TAssistantStore | null | undefined>
): TAccessCache => {
    const merged = new Map(known);
    stores.forEach((store) => {
        const storeId = getDocumentId(store as TAssistantStore);
        const enabled =
            typeof store === 'object' ? store?.businessAssistantEnabled : null;
        if (storeId && typeof enabled === 'boolean')
            merged.set(storeId, enabled);
    });
    return merged;
};

interface UseAssistantAccessParams {
    selectedLead: TSelectedLead | null;
    contextLeadId: string | null;
}

/**
 * Whether the assistant is visible and usable for the active chat, and which store it uses.
 */
export const useAssistantAccess = ({
    selectedLead,
    contextLeadId,
}: UseAssistantAccessParams) => {
    const { user } = useAuth() as { user: TAssistantUser | null };
    const knownAccessRef = useRef<TAccessCache>(new Map());
    const availableStores: TAssistantStore[] =
        user?.accessLevel === GROUP_LEVEL_ACCESS
            ? user?.group?.stores || []
            : user?.stores || [];
    const isBusinessAssistantEnabled = Boolean(
        user?.businessAssistantEnabled && user?.group?.businessAssistantEnabled
    );
    const generalStore = isBusinessAssistantEnabled
        ? availableStores.find((store) => store.businessAssistantEnabled) ||
          null
        : null;
    const selectedLeadStoreId = getDocumentId(
        selectedLead?.store as TAssistantStore
    );
    const selectedLeadStore = selectedLeadStoreId
        ? availableStores.find(
              (store) => getDocumentId(store) === selectedLeadStoreId
          ) || selectedLead.store
        : null;
    const activeStore = contextLeadId ? selectedLeadStore : generalStore;
    const activeStoreId = getDocumentId(activeStore as TAssistantStore) || null;

    const knownAccess = useMemo(
        () =>
            mergeStoreAccess(knownAccessRef.current, [
                ...availableStores,
                selectedLead?.store,
                activeStore,
            ]),
        [activeStore, availableStores, selectedLead]
    );

    useEffect(() => {
        knownAccessRef.current = knownAccess;
    }, [knownAccess]);

    return {
        groupId: getDocumentId(user?.group) || undefined,
        activeStoreId,
        assistantContextLoading: Boolean(contextLeadId && !selectedLeadStoreId),
        canUseAssistant: Boolean(
            isBusinessAssistantEnabled &&
                activeStoreId &&
                knownAccess.get(activeStoreId)
        ),
        assistantVisible: Boolean(
            isBusinessAssistantEnabled && (generalStore || selectedLead)
        ),
    };
};
